// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import {
  IStackItemStyles,
  IStackStyles,
  IStackTokens,
  IStyle,
  mergeStyles,
  IButtonStyles,
  Theme,
  IPanelStyles,
  IFocusTrapZoneProps,
  IRawStyle
} from '@fluentui/react';
import { pcpBluePalette } from "./themes/palette";

export const MEDIUM_SCREEN_BREAKPOINT = '960px';   //https://github.com/angular/flex-layout/wiki/Responsive-API

// /**
//  * @private
//  */
// export const configurationStackTokensDesktop: IStackTokens = {
//   childrenGap: '2rem'
// };

/**
 * @private
 */
export const configurationStackTokensMobile: IStackTokens = {
  childrenGap: '1rem'
};

export const configurationPageStyle = mergeStyles({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
} as IStyle);

export const callNotStartedHintStyle = mergeStyles({
    color: pcpBluePalette.foregroundDark,
    lineHeight: '19px',
    fontWeight: '400',
    fontSize: '14px',
    letterSpacing: '4%',
    marginTop: '8px',
});

const configurationPageContainerStyle: IStyle = {
  flexGrow: 1,
  flexShrink: 1,
  width: '100%',
  overflow: 'auto',
};

/**
 * @private
 */
export const configurationPageContainerStyleDesktop = mergeStyles({
  ...configurationPageContainerStyle,
  padding: '0.5rem',
});

/**
 * @private
 */
export const configurationPageContainerStyleMobile = mergeStyles({
  ...configurationPageContainerStyle,
  padding: '1rem',
});

const gridLayout = {
  root: {
    display: 'grid',
    gridColumnGap: '2em',
    gridTemplateColumns: '2fr 1fr',
    gridTemplateAreas: `'header .'
      'tile selection'`,
  },
  header: {
    gridArea: 'header',
  },
  localPreview: {
    gridArea: 'tile',
  } as IRawStyle,
  selectionContainer: {
    gridArea: 'selection',
  } as IRawStyle,
}

const callConfigurationStyles = {
  root: {
    maxWidth: '80rem',
    //align:
    marginLeft: 'auto',
    marginRight: 'auto',
    alignSelf: 'center',  //makes "height: auto" (instead of 100%)
    //align children elements:
    alignItems: 'stretch',
    width: '100%',
  } as IRawStyle,
};

export const callConfigurationMobileStyles = {
  ...callConfigurationStyles,
};

export const callConfigurationDesktopStyles = {
  ...callConfigurationStyles,
  root: Object.assign({}, callConfigurationStyles.root, gridLayout.root),
};

/**
 * @private
 */
export const selectionContainerDesktopStyle = mergeStyles({
  ...gridLayout.selectionContainer,
  minWidth: '11rem',
  padding: '0.5rem',
  marginTop: '0px',
});

export const selectionContainerDesktopOneColumnLayoutStyle = mergeStyles({
  minWidth: '11rem',
  padding: '0px 0.5rem 0.5rem 0.5rem',
  marginTop: '0px !important',
});

/**
 * @private
 */
export const localPreviewDesktopStyle: IRawStyle = {
  ...gridLayout.localPreview,
};

export const localPreviewDesktopOneColumnStyle = {
  padding: '0.5rem 0.5rem 0 0.5rem',
}

/**
 * @private
 */
export const titleContainerStyleDesktop = mergeStyles({
  fontSize: '1.25rem',
  lineHeight: '1.75rem',
  fontWeight: 600
});

/**
 * @private
 */
export const titleContainerStyleMobile = mergeStyles({
  fontSize: '1.0625rem',
  lineHeight: '1.375rem',
  fontWeight: 600,
  textAlign: 'center'
});

/**
 * @private
 */
export const callDetailsContainerStylesDesktop: IStackItemStyles = {
  root: {
    marginBottom: '1.563rem'
  }
};

const callDetailsStyle: IStyle = {
  fontSize: '0.9375',
  lineHeight: '1.25rem',
  marginTop: '0.25rem'
};

/**
 * @private
 */
export const callDetailsStyleDesktop = mergeStyles({
  ...callDetailsStyle
});

/**
 * @private
 */
export const callDetailsStyleMobile = mergeStyles({
  ...callDetailsStyle,
  textAlign: 'center'
});

/**
 * @private
 */
export const startCallButtonContainerStyleDesktop: IStackStyles = {
  root: {
    paddingTop: '1.5rem',
  }
};

/**
 * @private
 */
export const startCallButtonContainerStyleMobile: IStackStyles = {
  root: {
    textAlign: 'center'
  }
};

/**
 * @private
 */
export const startCallButtonStyleMobile = mergeStyles({
  width: '100%',
  maxWidth: 'unset'
});

/** @private */
export const cameraAndVideoEffectsContainerStyleDesktop: IStackItemStyles = {
  root: {
    alignItems: 'center'
  }
};

/** @private */
export const configurationPageContentStyleDesktop = mergeStyles({
  width: '100%',
  minWidth: '25rem', // sum of min-width from children + ChildrenGap * (nb of children - 1) + padding * 2 = (11 + 11) + (2 * 1) + 0.5 * 2
  minHeight: '22rem', // max height of SelectionContainer + padding * 2 = 21 + 0.5 * 2
  flexShrink: 0,
});

/** @private */
export const configurationPageContentStyleMobile = mergeStyles({
  width: '100%',
  minWidth: '19rem', //fits the minimum width of inner content. Dropdown-components in the "Einstellungen" field-set are the widest (e.g. <ConfigurationPageDeviceDropdown>)
  height: 'auto'
})

/** @private */
export const panelStyles: Partial<IPanelStyles> = {
  content: {
    display: 'flex',
    flexBasis: '100%',
    paddingLeft: '0rem', // remove default padding
    paddingRight: '0rem' // remove default padding
  },
  commands: {
    paddingTop: '0rem' // remove default padding
  },
  scrollableContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  }
};

/** @private */
export const panelFocusProps: IFocusTrapZoneProps = {
  forceFocusInsideTrap: false
};

export const fieldsetMargin = mergeStyles({
  marginTop: "1.5rem",
})
